import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { sanitizeArtifactContent } from "../utils/sanitizeArtifact";

const STAGE_LABELS: Record<string, string> = {
  business_case: "Business Case",
  product_spec: "Product Specification",
  architecture: "Technical Architecture",
  epic_plan: "Epic Plan",
};

interface ArtifactViewerProps {
  stage: string;
  content: string;
  onClose: () => void;
  onDownload?: () => void | Promise<void>;
}

export default function ArtifactViewer({
  stage,
  content,
  onClose,
  onDownload,
}: ArtifactViewerProps) {
  const [downloading, setDownloading] = useState(false);

  async function handleDownload() {
    if (!onDownload || downloading) return;
    setDownloading(true);
    try {
      await onDownload();
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
      <div className="bg-slate-900 border border-slate-700/50 rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700/50">
          <h2 className="text-lg font-semibold text-white">
            {STAGE_LABELS[stage] || stage}
          </h2>
          <div className="flex items-center gap-3">
            {onDownload && (
              <button
                onClick={handleDownload}
                disabled={downloading}
                className="inline-flex items-center gap-1.5 text-slate-400 hover:text-white disabled:text-slate-600 text-sm transition-colors"
              >
                {downloading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Download PDF
                  </>
                )}
              </button>
            )}
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-white transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          <div className="prose prose-invert prose-sm sm:prose-base max-w-none prose-headings:text-white prose-p:text-slate-300 prose-li:text-slate-300 prose-strong:text-white prose-table:text-sm">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{sanitizeArtifactContent(content)}</ReactMarkdown>
          </div>
        </div>
      </div>
    </div>
  );
}
